// Port de l'authentification/2FA de Client/js/app.js (lignes 445-628) — même contrat
// d'API, même mécanique de jeton de confiance 30 jours en localStorage. Les bouts propres
// à Electron (bienvenue, demarrerApplication, journal de connexions) ne sont pas repris.
import { defineStore } from 'pinia'
import { apiFetch, extraireErreurApi, API_BASE } from '@/services/api'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    apiBase: API_BASE,
    sessionToken: null,
    authUsername: '',
    authPassword: '',
    resterConnecte: true,
    role: null,
    authentifie: false,
    authErreur: null,
    authEnCours: false,
    compteExiste: null,
    inscriptionsActivees: false,
    chargementInscriptionsActivees: false,
    modeInscription: false,
    authPasswordConfirmation: '',

    // --- 2FA ---
    attente2fa: false,
    attente2faSetup: false,
    defi2fa: null,
    code2faSaisi: '',
    seSouvenirAppareil2fa: true,
    totp2faModeAffichage: 'qr',
    totp2faSecret: '',
    demandeCodeNotificationEnCours: false,
    demandeCodeNotificationEnvoyee: false,

    // --- Code de vérification 2FA poussé en notification, panneau admin web (voir
    // Client/js/app.js demarrerPollingNotificationAdminPanel/verifierNotificationAdminPanel) ---
    notificationAdminPanelIntervalId: null,
    idsCodesNotificationAdminPanelVus: new Set(),
    codeAdminPanelAffiche: null,
    codeAdminPanelFermetureTimeoutId: null,
    codeAdminPanelCopie: false,
  }),
  getters: {
    estAdmin: (state) => state.role === 'admin',
  },
  actions: {
    async verifierCompteExiste() {
      try {
        const res = await fetch(`${this.apiBase}/api/auth/existe`)
        const body = await res.json()
        this.compteExiste = body.compteExiste
        this.inscriptionsActivees = !!body.inscriptionsActivees
      } catch {
        this.compteExiste = null
      }
    },
    basculerModeInscription() {
      this.modeInscription = !this.modeInscription
      this.authErreur = null
      this.authPassword = ''
      this.authPasswordConfirmation = ''
    },
    soumettreFormulaireAuth() {
      // Le premier lancement (compteExiste === false) passe toujours par connexion() —
      // c'est /api/auth/login qui crée le tout premier compte (admin), l'inscription
      // self-service ne concerne que les comptes suivants. Voir Client/js/app.js même nom.
      if (this.compteExiste !== false && this.modeInscription) this.inscription()
      else this.connexion()
    },
    async inscription() {
      if (!this.authUsername || !this.authPassword) return
      if (this.authPassword !== this.authPasswordConfirmation) {
        this.authErreur = 'Les deux mots de passe ne correspondent pas.'
        return
      }
      this.authEnCours = true
      this.authErreur = null
      try {
        const res = await fetch(`${this.apiBase}/api/auth/inscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: this.authUsername, password: this.authPassword }),
        })
        if (!res.ok) throw new Error(await extraireErreurApi(res))
        this.appliquerConnexionReussie(await res.json())
      } catch (err) {
        this.authErreur = err.message
      } finally {
        this.authEnCours = false
      }
    },
    async chargerInscriptionsActivees() {
      try {
        const res = await apiFetch(`${this.apiBase}/api/parametres/inscriptions`)
        if (!res.ok) throw new Error(await extraireErreurApi(res))
        this.inscriptionsActivees = (await res.json()).activees
      } catch (err) {
        this.authErreur = err.message
      }
    },
    async basculerInscriptionsActivees(valeur) {
      this.chargementInscriptionsActivees = true
      try {
        const res = await apiFetch(`${this.apiBase}/api/parametres/inscriptions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ activees: valeur }),
        })
        if (!res.ok) throw new Error(await extraireErreurApi(res))
        this.inscriptionsActivees = (await res.json()).activees
      } catch (err) {
        this.authErreur = err.message
      } finally {
        this.chargementInscriptionsActivees = false
      }
    },
    async verifierSessionExistante() {
      const token = localStorage.getItem('alexisos-session-token')
      if (!token) return false
      this.sessionToken = token
      try {
        const res = await apiFetch(`${this.apiBase}/api/auth/verifier`)
        if (res.ok) {
          const body = await res.json()
          this.authUsername = body.username
          this.role = body.role
          this.authentifie = true
          this.demarrerPollingNotificationAdminPanel()
          return true
        }
      } catch {
        // API injoignable au chargement — on retentera une connexion manuelle.
      }
      this.sessionToken = null
      localStorage.removeItem('alexisos-session-token')
      return false
    },
    async connexion() {
      if (!this.authUsername || !this.authPassword) return
      this.authEnCours = true
      this.authErreur = null
      try {
        const confianceToken = localStorage.getItem(`alexisos-confiance-2fa-${this.authUsername}`) || null
        const res = await fetch(`${this.apiBase}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: this.authUsername, password: this.authPassword, confianceToken }),
        })
        if (!res.ok) throw new Error(await extraireErreurApi(res))
        const body = await res.json()
        if (body.besoin2faSetup) {
          this.defi2fa = body.defi
          this.code2faSaisi = ''
          this.seSouvenirAppareil2fa = true
          this.totp2faModeAffichage = 'qr'
          this.attente2faSetup = true
          await this.genererSecret2faDefi()
          return
        }
        if (body.besoin2fa) {
          this.defi2fa = body.defi
          this.code2faSaisi = ''
          this.seSouvenirAppareil2fa = true
          this.demandeCodeNotificationEnvoyee = false
          this.attente2fa = true
          return
        }
        this.appliquerConnexionReussie(body)
      } catch (err) {
        this.authErreur = err.message
      } finally {
        this.authEnCours = false
      }
    },
    async verifierCode2fa() {
      if (!this.code2faSaisi.trim()) return
      this.authEnCours = true
      this.authErreur = null
      try {
        const res = await fetch(`${this.apiBase}/api/auth/2fa/verifier`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ defi: this.defi2fa, code: this.code2faSaisi.trim(), seSouvenir: this.seSouvenirAppareil2fa }),
        })
        if (!res.ok) throw new Error(await extraireErreurApi(res))
        this.appliquerConnexionReussie(await res.json())
      } catch (err) {
        this.authErreur = err.message
      } finally {
        this.authEnCours = false
      }
    },
    async genererSecret2faDefi() {
      this.authErreur = null
      try {
        const res = await fetch(`${this.apiBase}/api/auth/2fa/generer-defi`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ defi: this.defi2fa }),
        })
        if (!res.ok) throw new Error(await extraireErreurApi(res))
        this.totp2faSecret = (await res.json()).secret
      } catch (err) {
        this.authErreur = err.message
      }
    },
    async activerEtVerifier2faSetup() {
      if (!this.code2faSaisi.trim()) return
      this.authEnCours = true
      this.authErreur = null
      try {
        const res = await fetch(`${this.apiBase}/api/auth/2fa/activer-defi`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ defi: this.defi2fa, code: this.code2faSaisi.trim(), seSouvenir: this.seSouvenirAppareil2fa }),
        })
        if (!res.ok) throw new Error(await extraireErreurApi(res))
        this.appliquerConnexionReussie(await res.json())
      } catch (err) {
        this.authErreur = err.message
      } finally {
        this.authEnCours = false
      }
    },
    annuler2fa() {
      this.attente2fa = false
      this.attente2faSetup = false
      this.defi2fa = null
      this.code2faSaisi = ''
      this.totp2faSecret = ''
      this.demandeCodeNotificationEnvoyee = false
      this.authErreur = null
      this.authPassword = ''
    },
    async demanderCodeNotification2fa() {
      this.demandeCodeNotificationEnCours = true
      this.authErreur = null
      try {
        const res = await fetch(`${this.apiBase}/api/auth/2fa/notification/demander`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ defi: this.defi2fa }),
        })
        if (!res.ok) throw new Error(await extraireErreurApi(res))
        this.demandeCodeNotificationEnvoyee = true
      } catch (err) {
        this.authErreur = err.message
      } finally {
        setTimeout(() => { this.demandeCodeNotificationEnCours = false }, 10000)
      }
    },
    appliquerConnexionReussie(body) {
      this.sessionToken = body.token
      if (this.resterConnecte) {
        localStorage.setItem('alexisos-session-token', body.token)
      } else {
        localStorage.removeItem('alexisos-session-token')
      }
      if (body.confianceToken) {
        localStorage.setItem(`alexisos-confiance-2fa-${body.username}`, body.confianceToken)
      }
      this.authUsername = body.username
      this.role = body.role
      this.authPassword = ''
      this.authPasswordConfirmation = ''
      this.modeInscription = false
      this.attente2fa = false
      this.attente2faSetup = false
      this.defi2fa = null
      this.code2faSaisi = ''
      this.totp2faSecret = ''
      this.authentifie = true
      this.demarrerPollingNotificationAdminPanel()
    },
    demarrerPollingNotificationAdminPanel() {
      if (!this.estAdmin || this.notificationAdminPanelIntervalId) return
      this.notificationAdminPanelIntervalId = setInterval(() => this.verifierNotificationAdminPanel(), 5000)
    },
    arreterPollingNotificationAdminPanel() {
      clearInterval(this.notificationAdminPanelIntervalId)
      this.notificationAdminPanelIntervalId = null
    },
    async verifierNotificationAdminPanel() {
      try {
        const res = await apiFetch(`${this.apiBase}/api/auth/2fa/notification/en-attente`)
        if (!res.ok) return
        const { enAttente, id, code } = await res.json()
        if (!enAttente || this.idsCodesNotificationAdminPanelVus.has(id)) return
        this.idsCodesNotificationAdminPanelVus.add(id)
        this.afficherCodeAdminPanel(code)
      } catch {
        // API temporairement injoignable — sans conséquence, on retentera au prochain tour.
      }
    },
    afficherCodeAdminPanel(code) {
      this.codeAdminPanelAffiche = code
      this.codeAdminPanelCopie = false
      clearTimeout(this.codeAdminPanelFermetureTimeoutId)
      this.codeAdminPanelFermetureTimeoutId = setTimeout(() => this.fermerCodeAdminPanel(), 3 * 60 * 1000)
    },
    fermerCodeAdminPanel() {
      this.codeAdminPanelAffiche = null
      clearTimeout(this.codeAdminPanelFermetureTimeoutId)
    },
    copierCodeAdminPanel() {
      if (!this.codeAdminPanelAffiche) return
      navigator.clipboard.writeText(this.codeAdminPanelAffiche).then(() => {
        this.codeAdminPanelCopie = true
        setTimeout(() => { this.codeAdminPanelCopie = false }, 1500)
      })
    },
    uriOtpAuth2fa() {
      const label = encodeURIComponent(`AlexisOS:${this.authUsername}`)
      return `otpauth://totp/${label}?secret=${this.totp2faSecret}&issuer=AlexisOS&digits=6&period=30`
    },
    async deconnexion() {
      try {
        await apiFetch(`${this.apiBase}/api/auth/logout`, { method: 'POST' })
      } catch {
        // Peu importe si la requête échoue — on efface le jeton localement dans tous les cas.
      }
      localStorage.removeItem('alexisos-session-token')
      this.sessionToken = null
      this.authentifie = false
      this.role = null
      this.authUsername = ''
      this.arreterPollingNotificationAdminPanel()
    },
  },
})
